import {
  PreviewSubjectOption,
  ProcessedQuestionContent,
} from "../QuestionPreview/types"
import {
  DEFAULT_LEVEL_LABELS,
  enabledDepth,
  labelAt,
  normalizeLabels,
} from "../Subjects/detail/hierarchyUtils"
import { isQuestionHierarchyLevelRequired } from "../Questions/utils/hierarchyValidation"

interface QuestionDetailsV2Props {
  content: ProcessedQuestionContent
  subjects: PreviewSubjectOption[]
}

type MetadataRow = {
  label: string
  value: string
  required?: boolean
  variant?: "tag"
}

const displayValue = (value?: string | null) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : "—"
}

const QuestionDetailsV2 = ({ content, subjects }: QuestionDetailsV2Props) => {
  const selectedSubject = subjects.find((item) => item.value === content.subject)

  const getSubjectLabel = (subjectValue: string) =>
    subjects.find((item) => item.value === subjectValue)?.label ||
    subjectValue ||
    "—"

  const labels = normalizeLabels(
    selectedSubject?.level_labels && selectedSubject.level_labels.length
      ? selectedSubject.level_labels
      : DEFAULT_LEVEL_LABELS
  )
  const depth = enabledDepth(labels)
  const levelValues = [
    content.topic,
    content.level_2_name,
    content.level_3_name,
    content.level_4_name,
  ]

  const levelRows: MetadataRow[] = Array.from(
    { length: depth },
    (_, idx) => ({
      label: labelAt(labels, idx + 1),
      value: displayValue(levelValues[idx]),
      required: isQuestionHierarchyLevelRequired(idx + 1, depth),
    })
  )

  const rows: MetadataRow[] = [
    { label: "Subject", value: getSubjectLabel(content.subject), required: true },
    ...levelRows,
    { label: "Sub-parts", value: displayValue(content.subPartsText) },
    { label: "Level", value: displayValue(content.grade), required: true },
    {
      label: "Difficulty",
      value: displayValue(content.complexity),
      required: true,
      variant: "tag",
    },
    { label: "Source", value: displayValue(content.source) },
    { label: "Year", value: displayValue(content.year) },
    { label: "Marks", value: displayValue(content.marks) },
  ]

  return (
    <div className='right right--details'>
      <h3>Question details</h3>

      <div className='question_preview_v2__detail-list'>
        {rows.map(({ label, value, required, variant }) => (
          <div key={label} className='question_preview_v2__detail-row'>
            <span className='question_preview_v2__detail-label'>
              {label}
              {required && <span className='req'>*</span>}
            </span>
            <span
              className={
                "question_preview_v2__detail-value" +
                (variant === "tag" ? " is-tag" : "")
              }
            >
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default QuestionDetailsV2
